<div class="block">
  @if (reordering()) {
    <div class="mb-4 flex justify-end">
      <button
        appUiButton
        appearance="outline"
        tone="neutral"
        type="button"
        class="inline-flex items-center gap-2"
        (click)="bulkEdit.addPendingGroup()"
      >
        <span class="inline-flex h-6 w-6 items-center justify-center" aria-hidden="true">
          <app-ui-icon name="add" />
        </span>
        Group
      </button>
    </div>
  }
  <ul class="m-0 mb-20 list-none p-0" cdkDropList (cdkDropListDropped)="categorySortUpdate($event)">
    @for (category of renderedCategories(); track category.id) {
      <li
        [id]="category.id"
        class="my-6 block first:mt-2"
        cdkDrag
        cdkDropListGroup
        [cdkDragData]="category"
        [cdkDragDisabled]="!reordering() || category.id === 'UNCATEGORIZED' || isPendingGroup(category)"
      >
        @if (reordering() || category?.id !== 'UNCATEGORIZED' || category.buckets.length) {
          <section
            class="categories-list-card relative block overflow-visible rounded-lg border border-border bg-surface-elevated p-0 shadow-raised"
            [class.border-dashed]="isPendingGroup(category)"
          >
            <div [class]="reordering() ? categoryHeaderClass() : 'p-0'">
              @if (!reordering()) {
                <div class="flex min-h-category-group-row w-full items-center bg-transparent">
                  <button
                    type="button"
                    class="category-toggle-btn flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 bg-transparent py-4 pl-4 pr-2 text-left"
                    [attr.aria-expanded]="isExpanded(category)"
                    (click)="toggleCategory(category)"
                  >
                    <app-ui-icon
                      class="h-4 w-4 shrink-0 text-text-muted transition-transform duration-200"
                      [class.rotate-90]="isExpanded(category)"
                      name="chevron-right"
                    />
                    @if (category?.id !== 'UNCATEGORIZED') {
                      <svg
                        class="h-category-group-progress w-category-group-progress shrink-0"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="9.5"
                          fill="none"
                          [attr.stroke]="getCategoryProgressTrackColor(category)"
                          stroke-width="3.5"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="9.5"
                          fill="none"
                          [attr.stroke]="getCategoryProgressColor(category)"
                          stroke-width="3.5"
                          stroke-linecap="round"
                          [attr.stroke-dasharray]="categoryRingCircumference"
                          [attr.stroke-dashoffset]="getCategoryProgressOffset(category)"
                          transform="rotate(-90 12 12)"
                        />
                      </svg>
                    }
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-category-group-title font-extrabold text-text-title">{{
                        category.name
                      }}</span>
                      <span class="mt-1 flex min-w-0 items-center gap-2">
                        <!-- The excluded marker sits OUTSIDE the truncating span: it is the shortest and
                             last part of the line, so a clipped sentence would eat it first and the header
                             would silently stop explaining the gap between figure and sentence. -->
                        <span class="flex min-w-0 items-center text-category-group-meta tabular-nums text-text-muted">
                          <span class="truncate">
                            <!-- Each suffix carries its own leading &nbsp;: Angular drops whitespace between
                                 adjacent @if blocks, and template whitespace alone would double the gap
                                 whenever the preceding suffix is absent. -->
                            <span>{{ getCategoryRollup(category).detail }}</span>
                            @if (getCategoryRollup(category).fundSuffix; as fundSuffix) {
                              <span [class.text-status-danger]="fundSuffix.tone === 'danger'">&nbsp;{{ fundSuffix.text }}</span>
                            }
                          </span>
                          @if (getCategoryRollup(category).excludedSuffix; as excludedSuffix) {
                            <span class="shrink-0">&nbsp;{{ excludedSuffix }}</span>
                          }
                        </span>
                        @if (overBudgetCount(category) > 0) {
                          <span
                            class="category-over-budget-badge shrink-0 rounded-full border border-status-danger/25 bg-status-danger/10 px-1.5 py-0.5 text-category-group-badge font-extrabold text-status-danger"
                          >
                            {{ overBudgetCount(category) }} over
                          </span>
                        }
                      </span>
                    </span>
                    @if (category?.id !== 'UNCATEGORIZED') {
                      <span class="category-primary-figure flex shrink-0 flex-col items-end gap-0.5 pl-4">
                        <span
                          class="text-category-group-title font-extrabold tabular-nums"
                          [style.color]="getCategoryFigureColor(category)"
                          >{{ getCategoryRollup(category).figure.value }}</span
                        >
                        @if (getCategoryRollup(category).figure.label) {
                          <span class="text-category-group-badge font-semibold uppercase tracking-wide text-text-muted">{{
                            getCategoryRollup(category).figure.label
                          }}</span>
                        }
                      </span>
                    }
                  </button>
                  @if (category?.id !== 'UNCATEGORIZED') {
                    <button
                      appUiButton
                      appearance="ghost"
                      tone="neutral"
                      type="button"
                      class="category-actions-btn mr-2 h-9! min-h-9! w-9! min-w-9! shrink-0 rounded-full! p-0!"
                      aria-label="Open group actions"
                      [appUiDropdownMenuTrigger]="categoryActionsMenu"
                      align="end"
                      side="bottom"
                      (appUiDropdownMenuOpened)="activeMenuId.set(category.id)"
                      (appUiDropdownMenuClosed)="closeMenu()"
                    >
                      <app-ui-icon name="more-vertical" class="size-[18px] text-text-muted" />
                    </button>
                    <ng-template #categoryActionsMenu>
                      <div appUiDropdownMenu class="min-w-48" aria-label="Group actions">
                        <button
                          appUiDropdownMenuItem
                          type="button"
                          class="w-full justify-start"
                          (click)="openAddCategoryDialog()"
                        >
                          Add Group
                        </button>
                        <button
                          appUiDropdownMenuItem
                          type="button"
                          class="w-full justify-start"
                          (click)="closeMenu(); drawerService.openAddBucketDrawer(category.id)"
                        >
                          Add Budget
                        </button>
                        <button
                          appUiDropdownMenuItem
                          variant="destructive"
                          type="button"
                          class="w-full justify-start"
                          (click)="closeMenu(); confirmDeleteCategory(category)"
                        >
                          Delete Group
                        </button>
                      </div>
                    </ng-template>
                  }
                </div>
              }

              @if (reordering() && category.id !== 'UNCATEGORIZED') {
                <div class="min-w-0 flex-1">
                  @if (isPendingGroup(category)) {
                    <span class="mb-1 block text-xs font-semibold uppercase tracking-wide text-brand-primary"
                      >New group</span
                    >
                  }
                  <input
                    appUiInput
                    name="name"
                    type="text"
                    maxlength="24"
                    class="!m-0 !h-auto !min-h-0 block w-full !rounded-none !border-0 !border-b !border-border-default !bg-transparent !px-0 !py-1 !text-base !font-bold !leading-tight !text-text-title !shadow-none focus:!outline-none focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!border-brand-primary"
                    [ngModel]="bulkEdit.draftGroupName(category)"
                    (ngModelChange)="updateGroupName(category, $event)"
                    aria-label="Group name"
                  />
                  <span class="mt-0.5 block truncate text-category-group-meta tabular-nums text-text-muted">
                    @if (!isPendingGroup(category)) {
                      {{ getCategoryRollup(category).detail }}
                    }
                  </span>
                </div>
              }
              @if (reordering() && category?.id !== 'UNCATEGORIZED') {
                <div class="ml-auto grid grid-cols-[2.5rem_2.5rem] items-center justify-items-center gap-2">
                  <app-ui-icon-button
                    icon="add"
                    tone="neutral"
                    ariaLabel="Add budget to group"
                    (click)="bulkEdit.addPendingBucket(category, monthYear() ?? '')"
                  />
                  @if (isPendingGroup(category)) {
                    <app-ui-icon-button
                      icon="close"
                      tone="danger"
                      ariaLabel="Remove pending group"
                      (click)="removePendingGroup(category)"
                    />
                  } @else {
                    <span
                      class="category-drag-handle inline-flex h-9 w-9 items-center justify-center text-border-default"
                      cdkDragHandle
                      aria-hidden="true"
                    >
                      <span class="inline-flex h-category-group-drag-icon w-category-group-drag-icon">
                        <app-ui-icon name="drag-handle" />
                      </span>
                    </span>
                  }
                </div>
              }
            </div>
            @if (isExpanded(category)) {
              <div class="border-t border-border-muted">
                <app-category-bucket-list
                  [groupId]="category.id"
                  [reorder]="reordering()"
                  [buckets]="category.buckets"
                  [archiveCandidates]="archiveCandidates()"
                  [currentMonthYear]="(currentMonthYear$ | async) ?? ''"
                  [connectedListIds]="connectedListIds()"
                />
              </div>
            }
          </section>
        }
      </li>
    }
  </ul>
</div>
